import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { http } from "@/lib/api";

export default function RegisterPage() {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [password, setPassword] = useState("");
    const [newsletter, setNewsletter] = useState(true);
    const [busy, setBusy] = useState(false);
    const [gift, setGift] = useState(null);
    const { register } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        http.get("/newsletter/welcome-gift").then((r) => setGift(r.data)).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const res = await register(email, password, firstName.trim(), lastName.trim(), newsletter);
        setBusy(false);
        if (res.ok) {
            toast.success("Bienvenue dans la Foundation of Light");
            navigate("/espace");
        } else {
            toast.error(res.error);
        }
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center px-6 py-16" data-testid="register-page">
            <div className="w-full max-w-md">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Rejoindre</div>
                <h1 className="font-serif text-5xl mb-8">Créer un compte</h1>
                <form onSubmit={submit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="first-name" className="mb-2 block">Prénom</Label>
                            <Input
                                id="first-name"
                                required
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="h-12 rounded-full px-5"
                                data-testid="register-first-name"
                                autoComplete="given-name"
                            />
                        </div>
                        <div>
                            <Label htmlFor="last-name" className="mb-2 block">Nom</Label>
                            <Input
                                id="last-name"
                                required
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="h-12 rounded-full px-5"
                                data-testid="register-last-name"
                                autoComplete="family-name"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="email" className="mb-2 block">Email</Label>
                        <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-full px-5" data-testid="register-email" autoComplete="email" />
                    </div>
                    <div>
                        <Label htmlFor="password" className="mb-2 block">Mot de passe (min. 6)</Label>
                        <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-full px-5" data-testid="register-password" autoComplete="new-password" />
                    </div>
                    <label className="flex items-start gap-3 rounded-2xl border border-border/60 bg-muted/30 p-4 cursor-pointer hover:bg-muted/50 transition-colors" data-testid="register-newsletter-label">
                        <input
                            type="checkbox"
                            checked={newsletter}
                            onChange={(e) => setNewsletter(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-input accent-primary shrink-0 cursor-pointer"
                            data-testid="register-newsletter-checkbox"
                        />
                        <span className="text-sm text-foreground/80 leading-relaxed">
                            Je souhaite recevoir la newsletter (nouveautés, ateliers, transmissions). Désinscription possible à tout moment.
                        </span>
                    </label>
                    {gift?.is_active && newsletter && (
                        <div className="rounded-2xl border-2 border-[#c9a66b]/50 bg-[#c9a66b]/10 p-4 flex items-start gap-3" data-testid="register-gift-banner">
                            <Gift className="w-5 h-5 text-[#c9a66b] shrink-0 mt-0.5" />
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-[#c9a66b] font-semibold mb-1">🎁 Cadeau de bienvenue</div>
                                <p className="text-sm text-foreground/85 leading-snug">
                                    <strong>{gift.title}</strong>
                                    {gift.duration_minutes ? ` (${gift.duration_minutes} min)` : ""} — offert immédiatement dans votre email de bienvenue.
                                </p>
                            </div>
                        </div>
                    )}
                    <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-primary text-primary-foreground" data-testid="register-submit">
                        {busy ? "Création…" : "Créer mon compte"}
                    </Button>
                </form>
                <p className="mt-8 text-sm text-muted-foreground text-center">
                    Déjà membre ?{" "}
                    <Link to="/connexion" className="text-primary underline underline-offset-4" data-testid="goto-login">
                        Se connecter
                    </Link>
                </p>
            </div>
        </div>
    );
}
